import { Component, HostBinding, inject, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { filter, startWith, takeUntil } from 'rxjs/operators';
import { AresService } from '@app/services/ares.service';
import { AresInterface } from '@app/interfaces/ares.interface';
import { SnackbarService } from '@app/services/snackbar.service';
import { IncomeInterface } from '@app/interfaces/income.interface';
import { FormService } from '@app/services/form.service';
import { FormUtils } from '@app/utils/form-utils';
import { IncomeLocationEnum } from '@app/enums/income-location.enum';
import { IncomeTypeEnum } from '@app/enums/income-type.enum';
import { IncomeBeforeOfferConfig } from '@app/class/income-before-offer.config';
import { OfferStepsEnum } from '@app/enums/offer-steps.enum';
import { IncomeAfterOfferConfig } from '@app/class/income-after-offer.config';
import { IncomeConfig } from '@app/class/income.class';
import { ContractTermPeriodEnum } from '@app/enums/contract-term-period.enum';
import { ContactInterface } from '@app/interfaces/contact.interface';
import bind from 'bind-decorator';
import { LoaderService } from '@app/services/loader.service';
import { Subject } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { HasErrorDirective } from '@app/directives/validation/has-error.directive';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { InfoPanelComponent } from '@app/components/info-panel/info-panel.component';
import { HypoRadioComponent } from '@app/components/form-components/hypo-radio/hypo-radio.component';
import { HypoInputComponent } from '@app/components/form-components/hypo-input/hypo-input.component';
import { HypoDatepickerComponent } from '@app/components/form-components/hypo-datepicker/hypo-datepicker.component';
import { HypoSelectComponent } from '@app/components/form-components/hypo-select/hypo-select.component';
import { TextByTypePipe } from '@app/pipes/text-by-type.pipe';
import { ShouldShowInputPipe } from '@app/pipes/should-show-input.pipe';
import { ListByTypePipe } from '@app/pipes/list-by-type-pipe/list-by-type.pipe';
import { OptionPipe } from '@app/pipes/option-pipe/option.pipe';
import { MatLabel } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RichFormControlDirective } from '@app/directives/validation/rich-form-control.directive';
import { MatButton } from '@angular/material/button';
import { MortgagesService } from '@app/services/mortgages.service';
import { MortgageInterface } from '@app/interfaces/mortgage.interface';

@Component({
    selector: 'app-income-dialog',
    templateUrl: './income-dialog.component.html',
    styleUrls: ['./income-dialog.component.scss'],
    imports: [
        MatIconModule,
        HasErrorDirective,
        RichFormControlDirective,
        MatDialogModule,
        MatSlideToggleModule,
        InfoPanelComponent,
        HypoRadioComponent,
        HypoInputComponent,
        HypoDatepickerComponent,
        HypoSelectComponent,
        TextByTypePipe,
        ShouldShowInputPipe,
        ListByTypePipe,
        OptionPipe,
        ReactiveFormsModule,
        MatLabel,
        MatProgressSpinner,
        MatButton,
    ],
})
export class IncomeDialogComponent implements OnInit, OnDestroy {
    dialogRef = inject<MatDialogRef<IncomeDialogComponent>>(MatDialogRef);
    data = inject(MAT_DIALOG_DATA);

    protected readonly snackbarService = inject(SnackbarService);
    protected readonly formService = inject(FormService);
    protected readonly aresService = inject(AresService);
    protected readonly mortgagesService = inject(MortgagesService);
    public readonly loaderService = inject(LoaderService);

    protected destroyed$: Subject<void> = new Subject<void>();

    public blurContent = false;
    public applicant: ContactInterface;
    public income: IncomeInterface;
    public incomeDialogForm: UntypedFormGroup;
    @HostBinding('class.show-more') showMore = true;

    public IncomeLocationEnum: typeof IncomeLocationEnum = IncomeLocationEnum;

    public incomeConfig: IncomeConfig;
    public ContractTermPeriod: typeof ContractTermPeriodEnum = ContractTermPeriodEnum;

    get type() {
        return this.incomeDialogForm.get('type') as UntypedFormArray;
    }

    public close(data?: any) {
        this.dialogRef.close(data);
    }

    ngOnInit() {
        this.mortgagesService.mortgage$
            .pipe(
                filter((mortgage) => mortgage !== undefined),
                takeUntil(this.destroyed$)
            )
            .subscribe((mortgage: MortgageInterface) => {
                if (mortgage) {
                    if (mortgage.fillingScore < OfferStepsEnum.Offers) {
                        this.incomeConfig = new IncomeBeforeOfferConfig();
                    } else {
                        this.incomeConfig = new IncomeAfterOfferConfig();
                    }
                }
            });

        this.income = this.data?.income?.value;
        this.showMore = this.data?.showMore;
        this.applicant = this.data?.applicant;
        this.incomeDialogForm = this.formService.createIncomeForm(this.income, this.incomeConfig);
        this.bindValueChangesDetection();

        if (!this.showMore) {
            this.type.valueChanges
                .pipe(startWith(this.type.value as string), takeUntil(this.destroyed$))
                .subscribe((type: IncomeTypeEnum) => {
                    this.showMore = type === IncomeTypeEnum.turnoverIncome;
                });
        }

        this.blurContent = this.data?.blurContent;
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    public isRequiredByType(inputName: string): boolean {
        return FormUtils.isRequiredByType(this.incomeConfig.requiredOnTypes, inputName, this.type.value);
    }

    continue() {
        if (this.formService.hasValidVisibleControls(this.incomeDialogForm, true)) {
            const shareInTheCompany = this.incomeDialogForm.get('shareInTheCompany').value;
            if (shareInTheCompany) {
                this.incomeDialogForm.get('shareInTheCompany').patchValue(FormUtils.unmaskPercent(shareInTheCompany));
            }

            if (
                this.type.value === IncomeTypeEnum.futureRentalRevenue ||
                this.type.value === IncomeTypeEnum.rentalIncomeOutsideTaxReturn
            ) {
                const applicantFullName = this.applicant?.firstName + ' ' + this.applicant?.lastName;
                this.incomeDialogForm.get('source.name').patchValue(applicantFullName);
            }

            this.close(this.incomeDialogForm);
        }
    }

    public findAres(idNumber: string): void {
        if (!this.loaderService.loading()) {
            this.loaderService.loader(true);
            this.aresService
                .findAres(idNumber)
                .then((ares: AresInterface) => {
                    if (ares) {
                        this.incomeDialogForm.get('source.createdAt').patchValue(ares.createdAt);
                        this.incomeDialogForm.get('source.name').patchValue(ares.name);
                    }
                })
                .finally(() => {
                    this.loaderService.loader(false);
                });
        }
    }

    @bind
    public additionalIncomeTypeCondition(inputName: string, type: IncomeTypeEnum): boolean {
        switch (inputName) {
            case 'untilDate':
                switch (type) {
                    case IncomeTypeEnum.rentalIncomeOutsideTaxReturn:
                    case IncomeTypeEnum.futureRentalRevenue:
                    case IncomeTypeEnum.rentalIncome:
                        const contractTermPeriod: ContractTermPeriodEnum =
                            this.incomeDialogForm.get('contractTermPeriod').value;
                        return contractTermPeriod === ContractTermPeriodEnum.fixedTerm;
                }
                return true;
            default:
                return false;
        }
    }

    private bindValueChangesDetection() {
        const shareInTheCompany = this.incomeDialogForm.get('shareInTheCompany').value;
        if (shareInTheCompany) {
            this.incomeDialogForm.get('shareInTheCompany').patchValue(FormUtils.maskPercent(shareInTheCompany));
        }
        this.incomeDialogForm
            .get('type')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.incomeDialogForm.get('untilDate').updateValueAndValidity();
                this.incomeDialogForm.get('fromDate').updateValueAndValidity();
            });
        this.incomeDialogForm
            .get('isTrialPeriod')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.incomeDialogForm.get('isNoticePeriod').patchValue(false, { emitEvent: false });
            });
        this.incomeDialogForm
            .get('isNoticePeriod')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.incomeDialogForm.get('isTrialPeriod').patchValue(false, { emitEvent: false });
            });
    }
}
